import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Gift, Image as ImageIcon, Euro, Upload, Camera, X, Store, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { resolveImageUrl as resolveImg } from "../context/AppSettingsContext";
import BranchMultiSelect from "../components/BranchMultiSelect";
import AiImage from "../components/AiImage";

export default function Rewards() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rewards, setRewards] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filterBranchIds, setFilterBranchIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [galleryFor, setGalleryFor] = useState(null);
  const fileRef = useRef(null);
  const empty = { name: "", description: "", short_description: "", reward_code: "", ean_code: "", booking_code: "", points_cost: 100, redemption_mode: "points_only", contribution_eur: 0, image_url: "", images: [], image_thumbs: [], stock: -1, quantity: 0, active: true, branch_ids: [] };
  const [form, setForm] = useState(empty);

  const [collections, setCollections] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState(() => new URLSearchParams(window.location.search).get("collection") || "all");
  const defaultColl = collections.find((c) => c.is_default);

  const load = async () => {
    const params = filterBranchIds.length > 0 ? { branch_id: filterBranchIds.join(",") } : {};
    const { data } = await api.get("/rewards", { params });
    setRewards(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBranchIds.join(",")]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
    api.get("/collections").then((r) => setCollections(r.data || [])).catch(() => setCollections([]));
  }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const upUrls = [];
      const upThumbs = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", "reward");
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        upUrls.push(data.url);
        upThumbs.push(data.thumb_url || data.url);
      }
      setForm((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...upUrls],
        image_thumbs: [...(prev.image_thumbs || []), ...upThumbs],
      }));
      toast.success(`${upUrls.length} foto caricate`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = (idx) => setForm((prev) => ({
    ...prev,
    images: (prev.images || []).filter((_, i) => i !== idx),
    image_thumbs: (prev.image_thumbs || []).filter((_, i) => i !== idx),
  }));
  const moveImage = (idx, dir) => setForm((prev) => {
    const arr = [...(prev.images || [])];
    const th = [...(prev.image_thumbs || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    [th[idx], th[j]] = [th[j], th[idx]];
    return { ...prev, images: arr, image_thumbs: th };
  });

  const openEdit = (r) => {
    setEditing(r);
    const imgs = (r.images && r.images.length) ? r.images : (r.image_url ? [r.image_url] : []);
    const thumbs = r.image_thumbs && r.image_thumbs.length ? r.image_thumbs : imgs;
    setForm({ ...r, short_description: r.short_description || "", reward_code: r.reward_code || "", ean_code: r.ean_code || "", booking_code: r.booking_code || "",
      images: imgs, image_thumbs: thumbs, branch_ids: r.branch_ids || [], quantity: r.stock === -1 ? 0 : r.stock, quantity_touched: false });
    setOpen(true);
  };
  const openNew = () => { setEditing(null); setForm({ ...empty, collection_id: collectionFilter !== "all" && collectionFilter !== defaultColl?.id ? collectionFilter : "" }); setOpen(true); };

  // Quantità in UI: 0 = nessun limite (backend stock -1). Un premio esaurito (stock 0) resta 0 finché l'admin non lo riassortisce.
  const stockFromQuantity = () => {
    const q = Number(form.quantity) || 0;
    if (q > 0) return q;
    if (editing && editing.stock === 0 && !form.quantity_touched) return 0;
    return -1;
  };

  const submit = async () => {
    const images = (form.images || []).filter(Boolean);
    const image_thumbs = (form.image_thumbs || []).filter(Boolean);
    const { quantity, quantity_touched, ...rest } = form;
    const payload = { ...rest, stock: stockFromQuantity(), images, image_thumbs, image_url: images[0] || form.image_url || null, branch_ids: form.branch_ids || [],
      collection_id: form.collection_id || null,
      reward_code: (form.reward_code || "").trim(), ean_code: (form.ean_code || "").trim(), booking_code: (form.booking_code || "").trim() };
    try {
      if (editing) await api.patch(`/rewards/${editing.id}`, payload);
      else await api.post("/rewards", payload);
      setOpen(false);
      toast.success("Premio salvato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/rewards/${id}`);
    toast.success("Premio eliminato");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="rewards-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold">CATALOGO PREMI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Cosa possono vincere</h1>
          <p className="text-zinc-400 mt-1">Definisci il costo in punti, lo stock e lo stato di ogni premio.</p>
        </div>
        {isAdmin && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
            {branches.length > 1 && (
              <div className="w-full sm:w-56">
                <BranchMultiSelect
                  branches={branches}
                  value={filterBranchIds}
                  onChange={setFilterBranchIds}
                  testId="reward-filter-branch"
                />
              </div>
            )}
            <Button data-testid="reward-new-button" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
              <Plus className="h-4 w-4 mr-2" /> Nuovo premio
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="rewards-collection-filter">
        <span className="text-xs text-zinc-500">Raccolta:</span>
        <button onClick={() => setCollectionFilter("all")} className={`pill ${collectionFilter === "all" ? "pill-gold" : "pill-zinc"} cursor-pointer`} data-testid="rewards-collection-all">Tutte</button>
        {collections.map((c) => <button key={c.id} onClick={() => setCollectionFilter(c.id)} className={`pill ${collectionFilter === c.id ? "pill-gold" : "pill-zinc"} cursor-pointer`} style={collectionFilter === c.id ? { background: c.color, color: "#111" } : undefined} data-testid={`rewards-collection-${c.id}`}>{c.name}</button>)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rewards.length === 0 && <div className="text-zinc-500 col-span-full">Nessun premio ancora nel catalogo.</div>}
        {rewards.length > 0 && collectionFilter !== "all" && rewards.filter((r) => (r.collection_id || defaultColl?.id) === collectionFilter).length === 0 && (
          <div className="text-zinc-500 col-span-full rounded-xl border border-dashed border-zinc-800 p-6 text-center" data-testid="rewards-collection-empty">Nessun premio in questa raccolta: crea il primo con «Nuovo premio» (la raccolta è già preselezionata).</div>
        )}
        {rewards.filter((r) => collectionFilter === "all" || (r.collection_id || defaultColl?.id) === collectionFilter).map((r) => {
          const gallery = (r.images && r.images.length) ? r.images : (r.image_url ? [r.image_url] : []);
          const cover = gallery[0];
          const coll = collections.find((c) => c.id === (r.collection_id || defaultColl?.id));
          return (
          <div key={r.id} data-testid={`reward-card-${r.id}`} className="card-elev overflow-hidden flex flex-col">
            {coll && !coll.is_default && <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest" style={{ background: coll.color, color: "#111" }} data-testid={`reward-collection-badge-${r.id}`}>Raccolta {coll.name}</div>}
            <button
              type="button"
              data-testid={`reward-gallery-open-${r.id}`}
              onClick={() => gallery.length > 0 && setGalleryFor(r)}
              className="relative h-44 bg-gradient-to-br from-zinc-900 to-zinc-950 text-left"
            >
              {cover ? (
                <img src={resolveImg(cover)} alt={r.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-amber-300/40">
                  <Gift className="h-14 w-14" />
                </div>
              )}
              <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                <span className={`pill ${r.active ? "pill-mint" : "pill-muted"} backdrop-blur-md bg-black/50`}>
                  {r.active ? "Attivo" : "Off"}
                </span>
                {r.contribution_eur > 0 && (
                  <span className="pill pill-red backdrop-blur-md bg-black/50 font-mono">
                    + € {r.contribution_eur.toFixed(2)}
                  </span>
                )}
                {(r.branch_ids && r.branch_ids.length > 0) && (
                  <span
                    className="pill pill-gold backdrop-blur-md bg-black/50 text-[10px]"
                    title={r.branch_ids.map((id) => branches.find((b) => b.id === id)?.name || id).join(", ")}
                    data-testid={`reward-branches-badge-${r.id}`}
                  >
                    <Store className="h-3 w-3" /> {r.branch_ids.length} sede{r.branch_ids.length > 1 ? "i" : ""}
                  </span>
                )}
              </div>
              {gallery.length > 1 && (
                <span className="absolute top-3 right-3 pill pill-gold backdrop-blur-md bg-black/50 text-[10px]" data-testid={`reward-gallery-badge-${r.id}`}>
                  <Camera className="h-3 w-3" /> {gallery.length}
                </span>
              )}
            </button>
            <div className="p-5 flex flex-col gap-3 flex-1">
              <div>
                <div className="font-heading font-bold text-lg">{r.name}</div>
                <div className="flex flex-wrap gap-1.5 mt-0.5 mb-1 text-[10px] font-mono text-zinc-500" data-testid={`reward-codes-${r.id}`}>
                  {r.reward_code && <span className="pill pill-sky">{r.reward_code}</span>}
                  {r.ean_code && <span className="pill pill-muted">EAN {r.ean_code}</span>}
                  {r.booking_code && <span className="pill pill-muted">PREN. {r.booking_code}</span>}
                </div>
                <div className="text-sm text-zinc-400 line-clamp-2">{r.short_description || r.description}</div>
              </div>
              <div className="flex items-center justify-between border-t border-zinc-900 pt-3 mt-auto">
                <div className="metric-value text-2xl text-amber-300">{r.points_cost}<span className="text-xs text-zinc-500 ml-1">pt</span></div>
                <div className={`text-xs ${r.stock === 0 ? "text-red-300 font-semibold" : "text-zinc-500"}`} data-testid={`reward-stock-${r.id}`}>
                  {r.stock === -1 ? "Quantità: illimitata" : r.stock === 0 ? "Esaurito" : `Disponibili: ${r.stock}`}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2 pt-1">
                  <Button data-testid={`reward-edit-${r.id}`} variant="outline" size="sm" className="flex-1 border-zinc-800" onClick={() => openEdit(r)}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica
                  </Button>
                  <Button data-testid={`reward-delete-${r.id}`} variant="outline" size="sm" className="border-zinc-800 hover:border-red-500 hover:text-red-300" onClick={() => remove(r.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );})}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica premio" : "Nuovo premio"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300 mb-1 inline-block">Nome*</Label>
              <Input data-testid="reward-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-zinc-300 mb-1 inline-block">ID premio</Label>
                <Input data-testid="reward-form-code" value={form.reward_code} onChange={(e) => setForm({ ...form, reward_code: e.target.value.toUpperCase() })} placeholder="auto (PR-000001)" className="bg-zinc-950 border-zinc-800 font-mono" />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Codice EAN</Label>
                <Input data-testid="reward-form-ean" inputMode="numeric" maxLength={14} value={form.ean_code} onChange={(e) => setForm({ ...form, ean_code: e.target.value.replace(/\D/g, "") })} placeholder="8/12/13/14 cifre" className="bg-zinc-950 border-zinc-800 font-mono" />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Codice prenotazione</Label>
                <Input data-testid="reward-form-booking" value={form.booking_code} onChange={(e) => setForm({ ...form, booking_code: e.target.value.toUpperCase() })} placeholder="es. KB-MENU" className="bg-zinc-950 border-zinc-800 font-mono" />
              </div>
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Descrizione breve</Label>
              <Input data-testid="reward-form-short-description" maxLength={120} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} placeholder="Una riga, mostrata nel catalogo" className="bg-zinc-950 border-zinc-800" />
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Descrizione dettagliata</Label>
              <Textarea data-testid="reward-form-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Dettagli, condizioni, ingredienti…" className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                <Camera className="h-3 w-3" /> Photo gallery
              </Label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {(form.images || []).map((url, idx) => (
                  <div key={idx} className="relative group rounded-lg overflow-hidden border border-zinc-800 bg-zinc-950 aspect-square" data-testid={`reward-img-${idx}`}>
                    <img src={resolveImg(url)} alt={`img-${idx}`} className="w-full h-full object-cover" />
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 pill pill-gold text-[9px] backdrop-blur-md bg-black/50">Cover</span>
                    )}
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-1">
                      <div className="flex gap-1">
                        {idx > 0 && (
                          <button onClick={() => moveImage(idx, -1)} className="text-white text-xs bg-black/60 rounded px-1.5 py-0.5">←</button>
                        )}
                        {idx < (form.images.length - 1) && (
                          <button onClick={() => moveImage(idx, 1)} className="text-white text-xs bg-black/60 rounded px-1.5 py-0.5">→</button>
                        )}
                      </div>
                      <button data-testid={`reward-img-remove-${idx}`} onClick={() => removeImage(idx)} className="text-red-300 text-[10px] hover:text-red-100">
                        <Trash2 className="h-3 w-3 inline mr-0.5" /> Rimuovi
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  data-testid="reward-img-add"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-lg border-2 border-dashed border-zinc-700 hover:border-amber-400/60 text-zinc-500 hover:text-amber-300 flex flex-col items-center justify-center gap-1 text-[10px]"
                >
                  {uploading ? "Upload…" : (<><Plus className="h-5 w-5" /> Aggiungi</>)}
                </button>
              </div>
              <div className="mb-2">
                <AiImage
                  triggerLabel="Genera copertina con AI"
                  testid="rewards-ai-image"
                  defaultPrompt={form.name ? `Premio loyalty: ${form.name}. Cover quadrata, moderna, colori caldi, senza testo.` : ""}
                  promptHint="Descrivi il premio (es. Set griglia da barbecue, stile lifestyle)"
                  onGenerated={(url) => {
                    setForm((prev) => ({
                      ...prev,
                      images: [...(prev.images || []), url],
                      image_thumbs: [...(prev.image_thumbs || []), url],
                    }));
                    toast.success("Immagine aggiunta alla gallery");
                  }}
                />
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="reward-image-input"
                onChange={onUpload}
              />
              <p className="text-[11px] text-zinc-500">La prima immagine è la copertina · usa le frecce per riordinare.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-zinc-300 mb-1 inline-block">Raccolta punti (catalogo esclusivo)</Label>
                <Select value={form.collection_id || "__default__"} onValueChange={(v) => setForm({ ...form, collection_id: v === "__default__" ? "" : v })}>
                  <SelectTrigger data-testid="reward-form-collection" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {collections.map((c) => <SelectItem key={c.id} value={c.is_default ? "__default__" : c.id}>{c.name}{c.is_default ? " (principale)" : ""}{c.mode === "points_plus_cash" ? " · punti + contributo" : ""}</SelectItem>)}
                    {collections.length === 0 && <SelectItem value="__default__">Raccolta principale</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500 mt-1">Il premio si riscatta solo con i punti di questa raccolta.</p>
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Modalità riscatto</Label>
                <Select value={form.redemption_mode} onValueChange={(v) => setForm({ ...form, redemption_mode: v, contribution_eur: v === "points_only" ? 0 : (form.contribution_eur || 1) })}>
                  <SelectTrigger data-testid="reward-form-mode" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="points_only">Solo punti</SelectItem>
                    <SelectItem value="points_plus_cash">Punti + contributo</SelectItem>
                    <SelectItem value="both">Entrambe (doppio punteggio)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">{form.redemption_mode === "both" ? "Costo SOLO PUNTI (pt)" : "Costo (pt)"}</Label>
                <Input data-testid="reward-form-cost" type="number" value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: parseInt(e.target.value || "0") })} className="bg-zinc-950 border-zinc-800" />
              </div>
              {form.redemption_mode === "both" && (
                <div className="col-span-2"><Label className="text-zinc-300 mb-1 inline-block">Costo con CONTRIBUTO (pt) · il cliente sceglie: {form.points_cost} pt oppure {form.points_cost_cash || 0} pt + € {Number(form.contribution_eur || 0).toFixed(2)}</Label>
                  <Input data-testid="reward-form-cost-cash" type="number" value={form.points_cost_cash || ""} onChange={(e) => setForm({ ...form, points_cost_cash: parseInt(e.target.value || "0") })} className="bg-zinc-950 border-zinc-800" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(form.redemption_mode === "points_plus_cash" || form.redemption_mode === "both") && (
                <div>
                  <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                    <Euro className="h-3 w-3" /> Contributo cassa
                  </Label>
                  <Input data-testid="reward-form-contribution" type="number" step="0.01" min="0" value={form.contribution_eur} onChange={(e) => setForm({ ...form, contribution_eur: parseFloat(e.target.value || "0") })} className="bg-zinc-950 border-zinc-800" />
                </div>
              )}
              <div><Label className="text-zinc-300 mb-1 inline-block">Quantità disponibile (0 = nessun limite)</Label>
                <Input data-testid="reward-form-quantity" type="number" min={0} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Math.max(0, parseInt(e.target.value || "0")), quantity_touched: true })} className="bg-zinc-950 border-zinc-800" />
                <p className="text-[11px] text-zinc-500 mt-1" data-testid="reward-form-quantity-hint">
                  {editing && editing.stock === 0 && !form.quantity_touched ? "Premio esaurito: inserisci una quantità per riassortirlo" : Number(form.quantity) > 0 ? `Riscattabile ${form.quantity} volte, poi risulta esaurito` : "Nessun limite di riscatti"}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div>
                <div className="text-sm font-medium">Attivo</div>
                <div className="text-xs text-zinc-500">Visibile in catalogo online</div>
              </div>
              <Switch data-testid="reward-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            {branches.length > 0 && (
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2 mb-2">
                  <Store className="h-3 w-3 text-amber-300" /> Disponibile nelle sedi
                </div>
                <p className="text-[11px] text-zinc-500 mb-2">
                  Nessuna sede selezionata = premio disponibile in <b>tutte</b> le sedi.
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="reward-form-branches">
                  {branches.map((b) => {
                    const on = (form.branch_ids || []).includes(b.id);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`reward-form-branch-${b.id}`}
                        onClick={() => setForm((prev) => ({
                          ...prev,
                          branch_ids: on
                            ? (prev.branch_ids || []).filter((x) => x !== b.id)
                            : [...(prev.branch_ids || []), b.id],
                        }))}
                        className={`px-3 py-2 rounded-lg border text-left text-sm flex items-center gap-2 ${on ? "border-amber-500 bg-amber-500/10 text-amber-100" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"}`}
                      >
                        <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? "bg-amber-500/20 border-amber-500" : "border-zinc-700"}`}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        <span className="truncate">{b.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <Button data-testid="reward-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {editing ? "Salva" : "Crea premio"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Lightbox */}
      <RewardGalleryLightbox item={galleryFor} onClose={() => setGalleryFor(null)} />
    </div>
  );
}

function RewardGalleryLightbox({ item, onClose }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [item?.id]);
  if (!item) return null;
  const images = (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []);
  if (!images.length) return null;
  const url = images[idx];
  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % images.length); };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-3xl p-0 overflow-hidden" data-testid="reward-gallery-lightbox">
        <DialogHeader className="p-4 pb-2"><DialogTitle>{item.name}</DialogTitle></DialogHeader>
        <div className="relative bg-black">
          <img src={resolveImg(url)} alt={item.name} className="w-full max-h-[70vh] object-contain bg-black" data-testid={`reward-gallery-img-${idx}`} />
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="reward-gallery-prev">‹</button>
              <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="reward-gallery-next">›</button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((_, i) => (
                  <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-amber-300" : "bg-zinc-500/60"}`} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="p-3 flex gap-2 overflow-x-auto bg-zinc-950 border-t border-zinc-800">
          {images.map((u, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-14 w-14 rounded-md overflow-hidden border ${i === idx ? "border-amber-400" : "border-zinc-800"} shrink-0`}>
              <img src={resolveImg(u)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
